import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Card,
    Alert,
    Button,
    Col,
    DatePicker,
    Descriptions,
    Drawer,
    Empty,
    Form,
    Grid,
    Input,
    InputNumber,
    Modal,
    Row,
    Select,
    Space,
    Table,
    Tabs,
    Tag,
    Typography,
} from 'antd';
import {
    CheckCircleOutlined,
    CopyOutlined,
    EditOutlined,
    HistoryOutlined,
    ReloadOutlined,
    StopOutlined,
    SwapOutlined,
} from '@ant-design/icons';
import moment from 'moment';

import PageTitle from '@/components/PageTitle';
import TableActionBar from '@/components/TableActionBar';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import qs from 'qs';
import { cleanObject } from '@/utils/helpers';
import { checkPermission } from '@/utils/permission';
import { showCommissionBusinessError } from '@/utils/commission/errorPresenter';
import {
    changeCommissionPolicyStatus,
    cloneCommissionPolicy,
    compareCommissionPolicyVersions,
    createCommissionPolicyVersion,
    fetchCommissionPolicies,
    fetchCommissionPolicyDetail,
    saveCommissionPolicy,
    validateCommissionPolicyVersion,
} from '@/redux/actions/commissionPolicyActions';
import '@/assets/css/CommissionServicePolicyPage.css';
import { PolicyAuditHistory, PolicyVersionContent } from './CommissionPolicyDetailContent';
import usePolicyFormSave from './usePolicyFormSave';

const { Text } = Typography;
const { useBreakpoint } = Grid;

const routes = [
    {
        path: '/',
        breadcrumbName: 'Trang chủ',
    },
    {
        path: '/commission-service-policies',
        breadcrumbName: 'Chính sách dịch vụ',
    },
]

const statuses = {
    DRAFT: ['Bản nháp', 'default'],
    SCHEDULED: ['Đã lên lịch', 'gold'],
    ACTIVE: ['Đang áp dụng', 'green'],
    RETIRED: ['Đã ngừng', 'red'],
};

const actionOptions = [
    { value: 'SERVICE_PASS', label: 'Cho phép xử lý doanh thu' },
    { value: 'SERVICE_BLOCK', label: 'Chặn xử lý doanh thu' },
    { value: 'REDUCE_REVENUE_PERCENT', label: 'Giảm doanh thu theo tỷ lệ' },
    { value: 'REDUCE_REVENUE_FIXED', label: 'Giảm doanh thu theo số tiền' },
    { value: 'HOLD_REVENUE_PERCENT', label: 'Giữ doanh thu theo tỷ lệ' },
    { value: 'HOLD_REVENUE_FIXED', label: 'Giữ doanh thu theo số tiền' },
    { value: 'SPLIT_REVENUE_COMPONENTS', label: 'Chia doanh thu thành các phần việc' },
    { value: 'CAP_ELIGIBLE_REVENUE_BY_QUANTITY', label: 'Giới hạn doanh thu theo số lượng đã thực hiện' },
];

const recipientOptions = [
    { value: 'DEFAULT_ASSIGNMENT', label: 'Theo phân công phù hợp' },
    { value: 'IMPLANT_DOCTOR', label: 'Bác sĩ cắm trụ' },
    { value: 'PROSTHETIC_DOCTOR', label: 'Bác sĩ phục hình' },
];

const dispositionOptions = [
    { value: 'IMMEDIATE', label: 'Tính ngay' },
    { value: 'HOLD', label: 'Giữ lại' },
];

const required = text => ({
    required: true,
    message: `Vui lòng ${text}.`,
});

const latest = policy => (policy?.versions || [])[0] || {};

const conditionCode = fieldKey => String(fieldKey || '').split('.')[1];

const formatDate = value =>
    value ? moment(value).format('DD/MM/YYYY') : 'Không giới hạn';

const scopeValues = (version, dimension) => [
    ...new Set(
        (version?.clauses || []).flatMap(clause =>
            (clause.scopes || [])
                .filter(scope => scope.dimension === dimension)
                .flatMap(scope =>
                    String(scope.compare_value || '')
                        .split(',')
                        .filter(Boolean),
                ),
        ),
    ),
];

const optionLabels = (values, options) =>
    values.map(
        value =>
            (options || []).find(
                option => String(option.value) === String(value),
            )?.label || `Mục #${value}`,
    );

// Dùng cùng catalog backend cho điều kiện đầu vào và điều kiện mở doanh thu.
function ServiceConditionRows({ name, form, capabilities }) {
    const fields = (capabilities.condition_fields || []).filter(item =>
        ['BOOLEAN', 'NUMBER'].includes(item.value_type)
        && (!item.rule_levels || item.rule_levels.includes('SERVICE')));
    return <Form.List name={name}>
        {(rows, { add, remove }) => <Space direction="vertical" style={{ width: '100%' }}>
            {rows.map(row => <Form.Item noStyle shouldUpdate key={row.key}>
                {() => {
                    const current = form.getFieldValue([name, row.name]) || {};
                    const field = fields.find(item => item.value === current.field_key);
                    const operators = (capabilities.condition_operators || []).filter(item =>
                        !['IN', 'NOT_IN', 'BETWEEN'].includes(item.value)
                        && (!item.supported_value_types || item.supported_value_types.includes(field?.value_type)));
                    const needsValue = !['IS_TRUE', 'IS_FALSE', 'IS_NULL', 'IS_NOT_NULL'].includes(current.operator);
                    return <Row gutter={8}>
                        <Col span={10}><Form.Item name={[row.name, 'field_key']} label="Thông tin cần kiểm tra" rules={[required('chọn tiêu chí')]}>
                            <Select showSearch optionFilterProp="label" options={fields} onChange={value => {
                                const values = form.getFieldValue(name) || [];
                                values[row.name] = { field_key: value };
                                form.setFieldsValue({ [name]: values });
                            }} />
                        </Form.Item></Col>
                        <Col span={6}><Form.Item name={[row.name, 'operator']} label="Điều kiện đạt khi" rules={[required('chọn cách xét')]}><Select options={operators} /></Form.Item></Col>
                        {needsValue && <Col span={6}><Form.Item name={[row.name, 'value_number']} label="Giá trị" rules={[required('nhập giá trị')]}><InputNumber style={{ width: '100%' }} /></Form.Item></Col>}
                        <Col span={2}><Button type="text" danger onClick={() => remove(row.name)}>Xóa</Button></Col>
                    </Row>;
                }}
            </Form.Item>)}
            <Button type="dashed" onClick={() => add({})}>Thêm điều kiện</Button>
        </Space>}
    </Form.List>;
}

function ServiceScope({ version, capabilities, compact = false }) {
    const groups = optionLabels(
        scopeValues(version, 'service_group_id'),
        capabilities.catalogs?.service_groups,
    );
    const services = optionLabels(
        scopeValues(version, 'service_id'),
        capabilities.catalogs?.services,
    );
    const hasAllServices = (version?.clauses || []).some(
        clause =>
            !(clause.scopes || []).some(scope =>
                ['service_group_id', 'service_id'].includes(scope.dimension),
            ),
    );
    const labels = [
        ...(hasAllServices ? ['Tất cả dịch vụ'] : []),
        ...groups.map(label => `Nhóm: ${label}`),
        ...services,
    ];

    if (!labels.length) {
        return <Text type="secondary">Chưa chọn phạm vi dịch vụ</Text>;
    }

    const visibleLabels = compact ? labels.slice(0, 3) : labels;
    return (
        <Space size={[4, 4]} wrap>
            {visibleLabels.map(label => (
                <Tag key={label} color="blue">
                    {label}
                </Tag>
            ))}
            {compact && labels.length > 3 && (
                <Tag>+{labels.length - 3} phạm vi</Tag>
            )}
        </Space>
    );
}

const ServicePolicyForm = props => props.visible ? <ServicePolicyFormContent key={`${props.current?.id || 'new'}:${latest(props.current).id || 'new'}`} {...props} /> : null;

function ServicePolicyFormContent({
    visible,
    current,
    capabilities,
    onCancel,
    onSubmit,
}) {
    const screens = useBreakpoint();
    const [form] = Form.useForm();
    const { saving, save } = usePolicyFormSave(onSubmit, onCancel);

    const catalogs = capabilities.catalogs || {};

    const requirementOptions = useMemo(
        () =>
            (capabilities.condition_fields || []).filter(
                item =>
                    String(item.value).startsWith('conditions.') &&
                    String(item.value).endsWith('.is_satisfied'),
            ),
        [capabilities],
    );

    const releaseConditionOptions = useMemo(
        () =>
            requirementOptions.map(item => ({
                value: conditionCode(item.value),
                label: item.label,
            })),
        [requirementOptions],
    );

    const [initialValues] = useState(() => {
        if (!current) {
            return {
                effective_from: moment(),
                action_type: 'SERVICE_PASS',
                requirements: [],
                service_group_ids: [],
                service_ids: [],
                components: [],
                business_conditions: [],
                condition_logic: 'AND',
                release_conditions: [],
            };
        }

        const version = latest(current);
        const clause = (version.clauses || [])[0] || {};
        const action = (clause.actions || [])[0] || {};
        const config = action.value_json || {};
        const scopes = clause.scopes || [];

        return {
            policy_name: current.policy_name,
            effective_from: version.effective_from
                ? moment(version.effective_from)
                : moment(),
            effective_to: version.effective_to
                ? moment(version.effective_to)
                : null,
            change_reason: version.change_reason,
            service_group_ids: scopes
                .filter(item => item.dimension === 'service_group_id')
                .flatMap(item => String(item.compare_value).split(',')),
            service_ids: scopes
                .filter(item => item.dimension === 'service_id')
                .flatMap(item => String(item.compare_value).split(',')),
            business_conditions: clause.conditions || [],
            condition_logic: clause.conditions?.[0]?.group_logic || 'AND',
            action_type: action.action_type || 'SERVICE_PASS',
            amount: action.value_number,
            release_condition: config.release_steps?.[0]?.trigger_code,
            release_conditions: config.release_conditions || [],
            completed_condition_code: config.completed_condition_code,
            components: (config.components || []).map(item => ({
                ...item,
                release_condition: item.release_steps?.[0]?.trigger_code,
            })),
            notes: current.notes,
        };
    });

    const buildReleaseStep = code => ({
        code,
        label:
            requirementOptions.find(
                item => conditionCode(item.value) === code,
            )?.label || 'Điều kiện mở doanh thu',
        trigger_type: 'CONDITION',
        trigger_code: code,
        release_percent: 100,
    });

    const submit = values => {
        const sourceClauses = latest(current).clauses || [];
        const sourceClause = sourceClauses[0] || {};
        const sourceAction = sourceClause.actions?.[0];
        // Không chuyển âm thầm cấu hình ngoài khả năng của form thành điều kiện đơn giản.
        if ((sourceClause.conditions || []).some(item => Number(item.group_no) !== 1
            || !['BOOLEAN', 'NUMBER'].includes(item.value_type)
            || ['IN', 'NOT_IN', 'BETWEEN'].includes(item.operator))) {
            showCommissionBusinessError({
                operation: 'POLICY',
                problem: 'Chính sách chưa được lưu để bảo toàn cấu hình hiện tại.',
                causes: 'Chính sách có nhiều nhóm hoặc kiểu điều kiện mà biểu mẫu này chưa hỗ trợ chỉnh sửa an toàn.',
                nextActions: ['Đóng biểu mẫu và kiểm tra lại cấu hình hiện tại.', 'Liên hệ bộ phận phụ trách cấu hình nếu cần thay đổi chính sách này.'],
            });
            return;
        }
        const buildConditions = rows => (rows || []).map(item => ({
            field_key: item.field_key, operator: item.operator,
            value_type: (capabilities.condition_fields || []).find(field => field.value === item.field_key)?.value_type || item.value_type,
            ...(!['IS_TRUE', 'IS_FALSE', 'IS_NULL', 'IS_NOT_NULL'].includes(item.operator) ? { value_number: item.value_number } : {}),
        }));
        const action = {
            group_no: 1,
            action_type: values.action_type,
            value_type: 'NUMBER',
            value_number: Number(values.amount || 0),
        };

        if (['SERVICE_PASS', 'SERVICE_BLOCK'].includes(values.action_type)) {
            action.value_type = 'TEXT';
            action.value_text = values.notes || null;
            delete action.value_number;
        }

        if (
            ['HOLD_REVENUE_PERCENT', 'HOLD_REVENUE_FIXED'].includes(
                values.action_type,
            )
        ) {
            action.value_type = 'JSON';
            action.value_json = {
                release_conditions: buildConditions(values.release_conditions),
            };
        }

        if (values.action_type === 'CAP_ELIGIBLE_REVENUE_BY_QUANTITY') {
            action.value_type = 'JSON';
            delete action.value_number;
            action.value_json = {
                completed_condition_code: values.completed_condition_code,
                total_quantity_source: 'CUSTOMER_SERVICE_QUANTITY', service_value_source: 'CUSTOMER_SERVICE_SALE_PRICE'
            };
        }

        if (values.action_type === 'SPLIT_REVENUE_COMPONENTS') {
            action.value_type = 'JSON';
            delete action.value_number;

            action.value_json = {
                base_source: 'DISTRIBUTABLE_REVENUE',
                components: (values.components || []).map(item => ({
                    code: item.code,
                    label: item.label,
                    share_percent: Number(item.share_percent),
                    disposition: item.disposition,
                    recipient_source:
                        item.recipient_source || 'DEFAULT_ASSIGNMENT',
                    ...(item.disposition === 'HOLD'
                        ? {
                            release_steps: [
                                {
                                    ...buildReleaseStep(
                                        item.release_condition,
                                    ),
                                    label: 'Điều kiện mở phần doanh thu',
                                },
                            ],
                        }
                        : {}),
                })),
            };
        }

        // Chỉ thay phần cấu hình được form quản lý, không làm mất cấu hình nâng cao của cùng action.
        if (sourceAction?.action_type === action.action_type && action.value_json) {
            action.value_json = { ...sourceAction.value_json, ...action.value_json };
            if (String(action.action_type).startsWith('HOLD_REVENUE_')) delete action.value_json.release_steps;
        }

        const scopes = [
            ...(sourceClause.scopes || []).filter(item => !['service_group_id', 'service_id'].includes(item.dimension)),
            ...(values.service_group_ids?.length
                ? [
                    {
                        dimension: 'service_group_id',
                        operator: 'IN',
                        compare_value: values.service_group_ids,
                    },
                ]
                : []),
            ...(values.service_ids?.length
                ? [
                    {
                        dimension: 'service_id',
                        operator: 'IN',
                        compare_value: values.service_ids,
                    },
                ]
                : []),
        ];

        const conditions = buildConditions(values.business_conditions).map((item, index) => ({
            ...item, group_no: 1, group_logic: values.condition_logic || 'AND', sort_order: index,
        }));

        // Service Policy có thể chứa nhiều clause. Form hiện sửa clause đầu, các clause khác phải giữ nguyên.
        const remainingClauses = sourceClauses.slice(1).map(clause => ({
            clause_key: clause.rule_code?.split('__').pop(), name: clause.rule_name,
            rule_level: clause.rule_level, item_type: clause.item_type, item_type_label: clause.item_type_label,
            priority: clause.priority, description: clause.notes,
            scopes: clause.scopes || [], conditions: clause.conditions || [], actions: clause.actions || [],
        }));

        return save({
            policy_name: values.policy_name,
            beneficiary_type: 'SERVICE',
            effective_from: values.effective_from.format('YYYY-MM-DD'),
            effective_to:
                values.effective_to?.format('YYYY-MM-DD') || null,
            change_reason: values.change_reason,
            notes: values.notes,
            clauses: [
                {
                    clause_key: sourceClause.rule_code?.split('__').pop() || 'SERVICE_CASE',
                    name: values.policy_name,
                    rule_level: 'SERVICE',
                    item_type: sourceClause.item_type || 'SERVICE_EFFECT',
                    item_type_label: sourceClause.item_type_label,
                    priority: sourceClause.priority ?? 100,
                    description: sourceClause.notes,
                    scopes,
                    conditions,
                    actions: [action, ...(sourceClause.actions || []).slice(1)],
                },
                ...remainingClauses,
            ],
        });
    };
    return (
        <Drawer
            visible={visible}
            width={screens.md ? '80%' : '100%'}
            title={
                current?.id
                    ? 'Sửa bản nháp chính sách dịch vụ'
                    : 'Tạo chính sách dịch vụ'
            }
            onClose={onCancel}
            destroyOnClose
            className="commission-service-policy-form-drawer"
            footer={
                <div className="commission-service-policy-form-footer">
                    <Space>
                        <Button onClick={onCancel} size="large">
                            Hủy
                        </Button>
                        <Button
                            type="primary"
                            size="large"
                            loading={saving}
                            onClick={() => form.submit()}
                        >
                            Lưu bản nháp
                        </Button>
                    </Space>
                </div>
            }
        >
            <Form
                form={form}
                initialValues={initialValues}
                layout="vertical"
                onFinish={submit}
                preserve={false}
                className="commission-service-policy-form"
            >
                <Card
                    size="small"
                    title="1. Thông tin chính sách"
                    className="commission-service-policy-section-card"
                >
                    <Row gutter={[12, 0]} className="commission-service-policy-grid">
                        <Col xs={24} lg={12}>
                            <Form.Item
                                name="policy_name"
                                label="Tên chính sách dịch vụ"
                                rules={[required('nhập tên chính sách')]}
                            >
                                <Input placeholder="Ví dụ: Chia doanh thu Implant 70/30" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                            <Form.Item
                                name="effective_from"
                                label="Áp dụng từ ngày"
                                rules={[required('chọn ngày bắt đầu')]}
                            >
                                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                            <Form.Item name="effective_to" label="Ngừng áp dụng từ ngày">
                                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                <Card
                    size="small"
                    title="2. Phạm vi và điều kiện áp dụng"
                    className="commission-service-policy-section-card"
                >
                    <div className="commission-service-policy-subsection commission-service-policy-subsection--scope">
                        <div className="commission-service-policy-subsection__header">
                            <div>
                                <Text strong>Phạm vi dịch vụ</Text>
                                <div className="commission-service-policy-subsection__description">
                                    Chọn nhóm dịch vụ hoặc dịch vụ cụ thể mà chính sách này được áp dụng.
                                </div>
                            </div>
                        </div>
                        <Row gutter={[12, 0]} className="commission-service-policy-grid">
                            <Col xs={24} md={12}>
                                <Form.Item name="service_group_ids" label="Nhóm dịch vụ">
                                    <Select
                                        mode="multiple"
                                        showSearch
                                        optionFilterProp="label"
                                        options={catalogs.service_groups || []}
                                        placeholder="Để trống nếu áp dụng mọi nhóm"
                                    />
                                </Form.Item>
                            </Col>
                            {/* <Col xs={24} md={12}>
                                <Form.Item name="service_ids" label="Dịch vụ cụ thể">
                                    <Select
                                        mode="multiple"
                                        showSearch
                                        optionFilterProp="label"
                                        options={catalogs.services || []}
                                        placeholder="Để trống nếu áp dụng mọi dịch vụ"
                                    />
                                </Form.Item>
                            </Col> */}
                        </Row>
                    </div>

                    <div className="commission-service-policy-subsection commission-service-policy-subsection--condition">
                        <div className="commission-service-policy-subsection__header">
                            <div>
                                <Text strong>Điều kiện đầu vào</Text>
                                <div className="commission-service-policy-subsection__description">
                                    Chỉ xử lý doanh thu khi các xác nhận chuyên môn đã được ghi nhận.
                                </div>
                            </div>
                        </div>
                        <Form.Item name="condition_logic" label="Cách kết hợp điều kiện">
                            <Select options={[{ value: 'AND', label: 'Đồng thời đạt tất cả' }, { value: 'OR', label: 'Đạt ít nhất một' }]} />
                        </Form.Item>
                        <ServiceConditionRows name="business_conditions" form={form} capabilities={capabilities} />
                    </div>
                </Card>

                <Card
                    size="small"
                    title="3. Cách xử lý doanh thu"
                    className="commission-service-policy-section-card commission-service-policy-section-card--processing"
                >
                    <div className="commission-service-policy-subsection commission-service-policy-subsection--action">
                        <div className="commission-service-policy-subsection__header">
                            <div>
                                <Text strong>Hành động khi đạt điều kiện</Text>
                                <div className="commission-service-policy-subsection__description">
                                    Xác định cách hệ thống cho phép, chặn, giảm, giữ hoặc chia doanh thu.
                                </div>
                            </div>
                        </div>
                        <Row gutter={[12, 0]} className="commission-service-policy-grid">
                            <Form.Item noStyle shouldUpdate>
                                {({ getFieldValue }) => {
                                    const actionType = getFieldValue('action_type');
                                    const needsAmount = [
                                        'REDUCE_REVENUE_PERCENT',
                                        'REDUCE_REVENUE_FIXED',
                                        'HOLD_REVENUE_PERCENT',
                                        'HOLD_REVENUE_FIXED',
                                    ].includes(actionType);
                                    const isHold = String(actionType || '').startsWith('HOLD_REVENUE_');
                                    const isPercent = String(actionType || '').endsWith('PERCENT');

                                    return (
                                        <>
                                            <Col xs={24} md={needsAmount ? 12 : 24}>
                                                <Form.Item
                                                    name="action_type"
                                                    label="Khi đạt điều kiện, hệ thống sẽ"
                                                    rules={[required('chọn cách xử lý doanh thu')]}
                                                >
                                                    <Select options={actionOptions} />
                                                </Form.Item>
                                            </Col>
                                            {needsAmount && (
                                                <Col xs={24} md={12}>
                                                    <Form.Item
                                                        name="amount"
                                                        label={isPercent ? 'Tỷ lệ (%)' : 'Số tiền'}
                                                        rules={[required('nhập mức áp dụng')]}
                                                    >
                                                        <InputNumber
                                                            min={0}
                                                            max={isPercent ? 100 : undefined}
                                                            style={{ width: '100%' }}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            )}
                                            {isHold && (
                                                <Col span={24}>
                                                    <Text strong>Tự mở khi đồng thời đạt tất cả điều kiện dưới đây</Text>
                                                    <ServiceConditionRows name="release_conditions" form={form} capabilities={capabilities} />
                                                </Col>
                                            )}
                                            {actionType === 'CAP_ELIGIBLE_REVENUE_BY_QUANTITY' && <Col span={24}>
                                                <Form.Item name="completed_condition_code" label="Số lượng thực tế đã thực hiện" rules={[required('chọn điều kiện số lượng')]}>
                                                    <Select options={(capabilities.condition_fields || []).filter(item => String(item.value).startsWith('conditions.') && String(item.value).endsWith('.value_number')).map(item => ({ value: conditionCode(item.value), label: item.label }))} />
                                                </Form.Item>
                                                <Text type="secondary">Giới hạn lũy kế theo giá bán × số lượng đã thực hiện / tổng số lượng dịch vụ; chỉ ghi nhận phần chưa tính ở kỳ trước.</Text>
                                            </Col>}
                                        </>
                                    );
                                }}
                            </Form.Item>
                        </Row>
                    </div>

                    <Form.Item noStyle shouldUpdate>
                        {({ getFieldValue }) =>
                            getFieldValue('action_type') === 'SPLIT_REVENUE_COMPONENTS' ? (
                                <div className="commission-service-policy-subsection commission-service-policy-subsection--components">
                                    <div className="commission-service-policy-subsection__header">
                                        <div>
                                            <Text strong>Phân chia doanh thu theo phần việc</Text>
                                            <div className="commission-service-policy-subsection__description">
                                                Tổng tỷ lệ các phần doanh thu phải bằng 100%.
                                            </div>
                                        </div>
                                    </div>
                                    <Form.List name="components">
                                        {(fields, { add, remove }) => (
                                            <Space direction="vertical" size={10} style={{ width: '100%' }}>
                                                {fields.map((field, componentIndex) => (
                                                    <Card
                                                        key={field.key}
                                                        size="small"
                                                        title={`Phần doanh thu ${componentIndex + 1}`}
                                                        extra={
                                                            <Button
                                                                danger
                                                                type="text"
                                                                size="small"
                                                                onClick={() => remove(field.name)}
                                                            >
                                                                Xóa
                                                            </Button>
                                                        }
                                                        className="commission-service-policy-component-card"
                                                    >
                                                        <Row gutter={[12, 0]} className="commission-service-policy-grid">
                                                            <Col hidden>
                                                                <Form.Item
                                                                    name={[field.name, 'code']}
                                                                    hidden
                                                                >
                                                                    <Input />
                                                                </Form.Item>
                                                            </Col>
                                                            <Col xs={24} md={8}>
                                                                <Form.Item
                                                                    name={[field.name, 'label']}
                                                                    label="Tên phần việc"
                                                                    rules={[required('nhập tên phần việc')]}
                                                                >
                                                                    <Input />
                                                                </Form.Item>
                                                            </Col>
                                                            <Col xs={24} md={8}>
                                                                <Form.Item
                                                                    name={[field.name, 'share_percent']}
                                                                    label="Tỷ lệ (%)"
                                                                    rules={[required('nhập tỷ lệ')]}
                                                                >
                                                                    <InputNumber min={0} max={100} style={{ width: '100%' }} />
                                                                </Form.Item>
                                                            </Col>
                                                            <Col xs={24} md={12}>
                                                                <Form.Item
                                                                    name={[field.name, 'recipient_source']}
                                                                    label="Người thực hiện"
                                                                >
                                                                    <Select options={recipientOptions} />
                                                                </Form.Item>
                                                            </Col>
                                                            <Col xs={24} md={12}>
                                                                <Form.Item
                                                                    name={[field.name, 'disposition']}
                                                                    label="Xử lý"
                                                                >
                                                                    <Select options={dispositionOptions} />
                                                                </Form.Item>
                                                            </Col>
                                                            <Form.Item noStyle shouldUpdate>
                                                                {({ getFieldValue: read }) =>
                                                                    read([
                                                                        'components',
                                                                        field.name,
                                                                        'disposition',
                                                                    ]) === 'HOLD' ? (
                                                                        <Col span={24}>
                                                                            <Form.Item
                                                                                name={[field.name, 'release_condition']}
                                                                                label="Mở phần giữ khi"
                                                                                rules={[required('chọn điều kiện mở')]}
                                                                            >
                                                                                <Select
                                                                                    showSearch
                                                                                    optionFilterProp="label"
                                                                                    options={releaseConditionOptions}
                                                                                />
                                                                            </Form.Item>
                                                                        </Col>
                                                                    ) : null
                                                                }
                                                            </Form.Item>
                                                        </Row>
                                                    </Card>
                                                ))}
                                                <Button
                                                    type="dashed"
                                                    className="commission-service-policy-add-component"
                                                    onClick={() =>
                                                        add({
                                                            code: `part_${Date.now()}_${fields.length}`,
                                                            disposition: 'IMMEDIATE',
                                                            recipient_source: 'DEFAULT_ASSIGNMENT',
                                                        })
                                                    }
                                                >
                                                    Thêm phần doanh thu
                                                </Button>
                                            </Space>
                                        )}
                                    </Form.List>
                                </div>
                            ) : null
                        }
                    </Form.Item>
                </Card>

                <Card
                    size="small"
                    title="4. Ghi chú và lý do thay đổi"
                    className="commission-service-policy-section-card"
                >
                    <Row gutter={[12, 0]} className="commission-service-policy-grid">
                        <Col xs={24} md={12}>
                            <Form.Item
                                name="change_reason"
                                label="Lý do tạo/thay đổi"
                            >
                                <Input.TextArea rows={3} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="notes" label="Mô tả nghiệp vụ">
                                <Input.TextArea rows={3} />
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>
            </Form>
        </Drawer>
    );
}

function ServicePolicyDetail({ visible, policy, loading, capabilities, canViewHistory, onClose, onCompare }) {
    const screens = useBreakpoint();

    return (
        <Drawer
            visible={visible}
            width={screens.md ? '70%' : '100%'}
            title={policy?.policy_name || 'Chi tiết chính sách dịch vụ'}
            maskClosable={false}
            onClose={onClose}
            className="commission-service-policy-detail-drawer"
        >
            {loading ? (
                <Text>Đang tải thông tin...</Text>
            ) : policy ? (
                <>
                    <Descriptions
                        bordered
                        size="small"
                        column={1}
                        className="commission-service-policy-descriptions"
                    >
                        <Descriptions.Item label="Loại chính sách">
                            Áp dụng riêng cho dịch vụ
                        </Descriptions.Item>

                        <Descriptions.Item label="Ghi chú">
                            {policy.notes || 'Không có'}
                        </Descriptions.Item>
                    </Descriptions>
                    <Tabs defaultActiveKey="versions" className="commission-service-policy-detail-tabs">
                        <Tabs.TabPane tab="Các phiên bản" key="versions">
                            {(policy.versions || []).map((version, index) => {
                                return (
                                    <Card
                                        key={version.id}
                                        size="small"
                                        className="commission-service-policy-version-card"
                                        title={`Phiên bản ${version.version}`}
                                        extra={canViewHistory && index < policy.versions.length - 1 && (
                                            <Button icon={<SwapOutlined />} onClick={() => onCompare(version, policy.versions[index + 1])}>
                                                So sánh bản trước
                                            </Button>
                                        )}
                                    >
                                        <PolicyVersionContent version={version} capabilities={capabilities} />
                                    </Card>
                                );
                            })}
                        </Tabs.TabPane>
                        {canViewHistory && <Tabs.TabPane tab="Lịch sử thao tác" key="audit">
                            <PolicyAuditHistory events={policy.events} capabilities={capabilities} />
                        </Tabs.TabPane>}
                    </Tabs>
                </>
            ) : <Empty description="Không có dữ liệu chính sách" />}
        </Drawer>
    );
}

export default function CommissionServicePolicyPage() {
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const { policies, pagination, capabilities } = useSelector(state => state.commissionPolicyReducer);
    const canCreate = checkPermission('commission_config', 'create');
    const canEdit = checkPermission('commission_config', 'edit');
    const canChangeStatus = checkPermission('commission_config', 'status');
    const canCreateVersion = checkPermission('commission_config', 'create_version');
    const canViewDetail = checkPermission('commission_config', 'detail');
    const canViewHistory = checkPermission('commission_config', 'history');

    const filters = useMemo(() => {
        const query = qs.parse(location.search.slice(1));
        return cleanObject({
            ...query,
            beneficiary_type: 'SERVICE',
            include_system: 1,
            page: parseInt(query.page, 10) || 1,
            per_page: parseInt(query.per_page, 10) || 30,
        });
    }, [location.search]);

    const [loading, setLoading] = useState(false);
    const [visible, setVisible] = useState(false);
    const [editing, setEditing] = useState(null);
    const [detail, setDetail] = useState(null);
    const [detailVisible, setDetailVisible] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [comparison, setComparison] = useState(null);

    const pushFilters = nextFilters => {
        const query = cleanObject({ ...nextFilters });
        delete query.beneficiary_type;
        delete query.include_system;
        history.push({ pathname: location.pathname, search: qs.stringify(query) });
    };
    const changeFilter = (name, value) => pushFilters({ ...filters, [name]: value || undefined, page: 1 });
    const changeTable = (page, pageSize) => {
        const nextPageSize = pageSize || filters.per_page || 30;
        pushFilters({ ...filters, page: nextPageSize !== filters.per_page ? 1 : page, per_page: nextPageSize });
    };
    const load = useCallback(() => {
        setLoading(true);
        return dispatch(fetchCommissionPolicies(filters)).finally(() => setLoading(false));
    }, [dispatch, filters]);

    useEffect(() => { load(); }, [load]);

    const openDetail = row => {
        if (!canViewDetail) return;
        setDetailVisible(true);
        setDetailLoading(true);
        dispatch(fetchCommissionPolicyDetail(row.id)).then(setDetail).finally(() => setDetailLoading(false));
    };

    const askReason = (title, placeholder, action) => {
        const inputId = `service-policy-reason-${Date.now()}`;

        Modal.confirm({
            title,
            content: (
                <Input.TextArea
                    id={inputId}
                    rows={3}
                    placeholder={`${placeholder} (bắt buộc)`}
                />
            ),
            okText: 'Xác nhận',
            cancelText: 'Hủy',
            onOk: close => {
                const reason = document.getElementById(inputId)?.value?.trim();

                if (!reason) {
                    showCommissionBusinessError({ operation: 'POLICY', problem: 'Chưa thể thực hiện thay đổi trạng thái.', causes: 'Mô tả lý do thực hiện chưa được nhập.', nextActions: 'Nhập mô tả rõ ràng rồi xác nhận lại.' });
                    return;
                }

                return Promise.resolve(action(reason)).then(() => {
                    close();
                });
            },
        });
    };
    const validateAndActivate = (row, version) => dispatch(validateCommissionPolicyVersion(row.id, version.id)).then(result => {
        if (!result.valid) {
            showCommissionBusinessError({
                operation: 'POLICY_ACTIVATION',
                causes: result.errors,
                nextActions: ['Bổ sung hoặc điều chỉnh các nội dung được nêu.', 'Kiểm tra lại cấu hình trước khi kích hoạt.'],
            });
            return null;
        }
        return askReason('Kích hoạt chính sách dịch vụ?', 'Nhập lý do phê duyệt và kích hoạt', reason => dispatch(changeCommissionPolicyStatus(row.id, version.id, 'ACTIVE', reason)).then(load));
    });

    const clonePolicy = row => {
        const nameId = `service-clone-name-${row.id}`;
        const reasonId = `service-clone-reason-${row.id}`;

        Modal.confirm({
            title: 'Sao chép chính sách dịch vụ',
            width: 560,
            okText: 'Tạo bản sao',
            cancelText: 'Hủy',
            content: (
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Input
                        id={nameId}
                        defaultValue={`${row.policy_name} - Bản sao`}
                        placeholder="Tên chính sách mới"
                    />
                    <Input.TextArea
                        id={reasonId}
                        rows={2}
                        placeholder="Lý do sao chép (bắt buộc)"
                    />
                </Space>
            ),
            onOk: close => {
                const values = {
                    policy_name: document.getElementById(nameId)?.value?.trim(),
                    change_reason: document.getElementById(reasonId)?.value?.trim(),
                };

                if (!values.policy_name) {
                    showCommissionBusinessError({ operation: 'POLICY', problem: 'Chưa thể tạo bản sao chính sách.', causes: 'Tên chính sách mới chưa được nhập.', nextActions: 'Nhập tên chính sách mới rồi chọn “Tạo bản sao”.' });
                    return;
                }

                if (!values.change_reason) {
                    showCommissionBusinessError({ operation: 'POLICY', problem: 'Chưa thể tạo bản sao chính sách.', causes: 'Lý do sao chép chưa được nhập.', nextActions: 'Nhập lý do để bảo đảm lịch sử thay đổi rồi thử lại.' });
                    return;
                }

                return dispatch(cloneCommissionPolicy(row.id, values))
                    .then(() => {
                        load();
                        close();
                    });
            },
        });
    };
    const savePolicy = values => {
        return dispatch(saveCommissionPolicy(editing, values)).then(result => {
            load();
            return result;
        });
    };

    const columns = [
        {
            title: 'Chính sách dịch vụ',
            dataIndex: 'policy_name',
            key: 'policy_name',
            render: (value, row) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {canViewDetail ? (
                        <Button
                            type="link"
                            style={{
                                padding: 0,
                                height: 'auto',
                                fontWeight: 600,
                                textAlign: 'left',
                                justifyContent: 'flex-start',
                            }}
                            onClick={() => openDetail(row)}
                        >
                            {value}
                        </Button>
                    ) : (
                        <Text strong>{value}</Text>
                    )}

                    <div>
                        <Text
                            type="secondary"
                            style={{ fontSize: 12, marginRight: 6 }}
                        >
                            Phạm vi:
                        </Text>

                        <ServiceScope
                            version={latest(row)}
                            capabilities={capabilities}
                            compact
                        />
                    </div>
                </div>
            ),
        },
        { title: 'Phiên bản', key: 'version', render: (_, row) => `v${latest(row).version || '-'}` },
        { title: 'Thời gian áp dụng', key: 'effective', render: (_, row) => <><Text>{formatDate(latest(row).effective_from)}</Text><br /><Text type="secondary">đến {formatDate(latest(row).effective_to)}</Text></> },
        { title: 'Trạng thái', key: 'status', render: (_, row) => { const value = latest(row).status; const view = statuses[value] || [value, 'default']; return <Tag color={view[1]}>{view[0]}</Tag>; } },
        {
            title: 'Thao tác', key: 'actions', width: 300, render: (_, row) => {
                const version = latest(row);
                return <Space size={8} wrap>
                    {canEdit && version.status === 'DRAFT' && <Button size="small" icon={<EditOutlined />} onClick={() => { setEditing(row); setVisible(true); }}>Sửa</Button>}
                    {canChangeStatus && version.status === 'DRAFT' && <Button size="small" type="primary" icon={<CheckCircleOutlined />} onClick={() => validateAndActivate(row, version)}>Kiểm tra và kích hoạt</Button>}
                    {canCreateVersion && ['SCHEDULED', 'ACTIVE', 'RETIRED'].includes(version.status) && <Button size="small" icon={<HistoryOutlined />} onClick={() => askReason('Tạo phiên bản mới?', 'Mô tả nội dung dự kiến thay đổi', reason => dispatch(createCommissionPolicyVersion(row.id, { change_reason: reason })).then(load))}>Tạo phiên bản mới</Button>}
                    {canChangeStatus && version.status === 'SCHEDULED' && <Button size="small" onClick={() => askReason('Hủy lịch áp dụng?', 'Nhập lý do hủy lịch', reason => dispatch(changeCommissionPolicyStatus(row.id, version.id, 'DRAFT', reason)).then(load))}>Hủy lịch</Button>}
                    {canChangeStatus && version.status === 'ACTIVE' && <Button size="small" danger icon={<StopOutlined />} onClick={() => askReason('Ngừng áp dụng chính sách?', 'Nhập lý do ngừng áp dụng', reason => dispatch(changeCommissionPolicyStatus(row.id, version.id, 'RETIRED', reason)).then(load))}>Ngừng</Button>}
                    {canCreate && <Button size="small" icon={<CopyOutlined />} onClick={() => clonePolicy(row)}>Sao chép</Button>}
                </Space>;
            },
        },
    ];

    const filterStyle = { minWidth: isMobile ? '100%' : 220 };
    return (
        <div className="commission-service-policy-page">
            <PageTitle routes={routes} title="Chính sách dịch vụ" />
            <TableActionBar
                isShowAddButton={canCreate}
                isShowDeleteButton={false}
                onAdd={() => { setEditing(null); setVisible(true); }}
                showFilter={false}
                onSearch={keyword => changeFilter('keyword', keyword)}
                defaultKeyword={filters.keyword}
            >
                <Select allowClear className="table-button" value={filters.status} placeholder="Trạng thái" style={filterStyle} options={Object.entries(statuses).map(([value, config]) => ({ value, label: config[0] }))} onChange={value => changeFilter('status', value)} />
                <Select allowClear showSearch optionFilterProp="label" className="table-button" value={filters.service_group_id ? String(filters.service_group_id) : undefined} placeholder="Nhóm dịch vụ" style={filterStyle} options={capabilities.catalogs?.service_groups || []} onChange={value => changeFilter('service_group_id', value)} />
                <Select allowClear showSearch optionFilterProp="label" className="table-button" value={filters.service_id ? String(filters.service_id) : undefined} placeholder="Dịch vụ cụ thể" style={filterStyle} options={capabilities.catalogs?.services || []} onChange={value => changeFilter('service_id', value)} />
                <Button block={isMobile} className="table-button" icon={<ReloadOutlined />} loading={loading} onClick={load}>Làm mới</Button>
            </TableActionBar>
            <Alert className="commission-service-policy-alert mb-3" type="warning" message="Đây là chính sách áp dụng riêng cho dịch vụ: thiết lập điều kiện, giảm trừ, giữ/mở hoặc chia doanh thu trước khi xác định người hưởng hoa hồng. Chính sách không chọn phạm vi sẽ áp dụng cho tất cả dịch vụ phù hợp." />
            <Table size="small" rowKey="id" loading={loading} columns={columns} dataSource={policies} locale={{ emptyText: <Empty description="Chưa có chính sách dịch vụ phù hợp" /> }} pagination={{ current: pagination.current_page || filters.page || 1, total: pagination.total || 0, pageSize: pagination.per_page || filters.per_page || 30, showSizeChanger: true, onChange: changeTable, onShowSizeChange: changeTable }} scroll={{ x: 'max-content' }} />
            <ServicePolicyForm visible={visible} current={editing} capabilities={capabilities} onCancel={() => { setVisible(false); setEditing(null); }} onSubmit={savePolicy} />
            <ServicePolicyDetail visible={detailVisible} policy={detail} loading={detailLoading} capabilities={capabilities} canViewHistory={canViewHistory} onClose={() => { setDetailVisible(false); setDetail(null); }} onCompare={(version, previous) => dispatch(compareCommissionPolicyVersions(detail.id, version.id, previous.id)).then(setComparison)} />
            <Modal visible={Boolean(comparison)} width={isMobile ? '100%' : '70%'} title={comparison ? `So sánh phiên bản ${comparison.from.version} và ${comparison.to.version}` : 'So sánh phiên bản'} footer={<Button onClick={() => setComparison(null)}>Đóng</Button>} onCancel={() => setComparison(null)}>
                {comparison && (comparison.changes || []).length ? <Table size="small" rowKey={(row, index) => `${row.area}-${index}`} pagination={false} dataSource={comparison.changes} columns={[{ title: 'Nội dung thay đổi', dataIndex: 'area' }, { title: `Phiên bản ${comparison.from.version}`, dataIndex: 'before', render: value => value || 'Không có' }, { title: `Phiên bản ${comparison.to.version}`, dataIndex: 'after', render: value => value || 'Không có' }]} /> : <Empty description="Hai phiên bản không có khác biệt được ghi nhận" />}
            </Modal>
        </div>
    );
}
